import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "modal",
    "image",
    "cropBox",
    "overlay",
    "circlePreview",
    "profileXInput",
    "profileYInput",
    "profileWidthInput",
    "avatarXInput",
    "avatarYInput",
    "avatarWidthInput",
    "previewImage",
    "profilePreviewRing",
    "avatarPreviewRing",
    "profileTab",
    "avatarTab",
    "container",
    "imageContainer",
    "legend",
    "cropControls",
    "cropButton",
    "fileName",
  ];
  static values = {
    imageUrl: String,
    profileCropX: { type: Number, default: null },
    profileCropY: { type: Number, default: null },
    profileCropWidth: { type: Number, default: null },
    avatarCropX: { type: Number, default: null },
    avatarCropY: { type: Number, default: null },
    avatarCropWidth: { type: Number, default: null },
    activeTab: { type: String, default: "profile" },
  };

  connect() {
    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = null;
    this.startXPixels = 0;
    this.startYPixels = 0;
    this.startCropXPixels = 0;
    this.startCropYPixels = 0;
    this.startCropSizePixels = 0;
    this.minCropSizePixels = 50;

    this.imageOffsetXPixels = 0;
    this.imageOffsetYPixels = 0;

    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundKeyDown = this.handleKeyDown.bind(this);
  }

  disconnect() {
    this.removeGlobalListeners();
  }

  handleFileSelect(event) {
    const file = event.target.files[0];

    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file ? file.name : "No file chosen";
    }

    if (!file) return;

    const objectUrl = URL.createObjectURL(file);

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = objectUrl;
    }

    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.style.display = "";
    }

    if (this.hasLegendTarget) {
      this.legendTarget.style.display = "";
    }

    if (this.hasCropControlsTarget) {
      this.cropControlsTarget.style.display = "";
    }

    if (this.hasCropButtonTarget) {
      this.cropButtonTarget.dataset.imageUrl = objectUrl;
    }
  }

  openModal(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const imageUrl = button.dataset.imageUrl || this.imageUrlValue;

    if (!imageUrl) {
      console.error("No image URL provided");
      return;
    }

    const profileCrop = button.dataset.profileCrop;
    if (profileCrop && profileCrop !== "null" && profileCrop !== "{}") {
      try {
        const cropData = JSON.parse(profileCrop);
        this.profileCropXValue = cropData.x;
        this.profileCropYValue = cropData.y;
        this.profileCropWidthValue = cropData.width;
      } catch (e) {
        console.warn("Could not parse profile crop data:", e);
      }
    }

    const avatarCrop = button.dataset.avatarCrop;
    if (avatarCrop && avatarCrop !== "null" && avatarCrop !== "{}") {
      try {
        const cropData = JSON.parse(avatarCrop);
        this.avatarCropXValue = cropData.x;
        this.avatarCropYValue = cropData.y;
        this.avatarCropWidthValue = cropData.width;
      } catch (e) {
        console.warn("Could not parse avatar crop data:", e);
      }
    }

    this.imageUrlValue = imageUrl;
    this.showModal();
  }

  showModal() {
    this.imageTarget.src = this.imageUrlValue;
    this.imageTarget.onload = () => {
      this.modalTarget.style.display = "flex";
      document.body.style.overflow = "hidden";

      requestAnimationFrame(() => {
        this.switchToProfileTab();
        this.addGlobalListeners();
      });
    };
  }

  switchToProfileTab() {
    this.saveCurrentCropToState();
    this.activeTabValue = "profile";
    this.updateTabStyles();
    this.loadCropFromState();
  }

  switchToAvatarTab() {
    this.saveCurrentCropToState();
    this.activeTabValue = "avatar";
    this.updateTabStyles();
    this.loadCropFromState();
  }

  updateTabStyles() {
    if (this.hasProfileTabTarget && this.hasAvatarTabTarget) {
      if (this.activeTabValue === "profile") {
        this.profileTabTarget.classList.add("active");
        this.avatarTabTarget.classList.remove("active");
        this.cropBoxTarget.classList.remove("crop-box-avatar");
        this.cropBoxTarget.classList.add("crop-box-profile");
      } else {
        this.avatarTabTarget.classList.add("active");
        this.profileTabTarget.classList.remove("active");
        this.cropBoxTarget.classList.remove("crop-box-profile");
        this.cropBoxTarget.classList.add("crop-box-avatar");
      }
    }
  }

  saveCurrentCropToState() {
    if (this.currentCropXPixels == null) return;

    const img = this.imageTarget;
    const imgWidthPixels = img.offsetWidth;
    const imgHeightPixels = img.offsetHeight;

    const xPercentage = this.currentCropXPixels / imgWidthPixels;
    const yPercentage = this.currentCropYPixels / imgHeightPixels;
    const widthPercentage = this.currentCropSizePixels / imgWidthPixels;

    if (this.activeTabValue === "profile") {
      this.profileCropXValue = xPercentage;
      this.profileCropYValue = yPercentage;
      this.profileCropWidthValue = widthPercentage;
    } else {
      this.avatarCropXValue = xPercentage;
      this.avatarCropYValue = yPercentage;
      this.avatarCropWidthValue = widthPercentage;
    }
  }

  loadCropFromState() {
    const img = this.imageTarget;
    const imgWidthPixels = img.offsetWidth;
    const imgHeightPixels = img.offsetHeight;

    this.imageOffsetXPixels = img.offsetLeft;
    this.imageOffsetYPixels = img.offsetTop;

    let cropXPercentage, cropYPercentage, cropWidthPercentage;

    if (this.activeTabValue === "profile") {
      cropXPercentage = this.profileCropXValue;
      cropYPercentage = this.profileCropYValue;
      cropWidthPercentage = this.profileCropWidthValue;
    } else {
      cropXPercentage = this.avatarCropXValue;
      cropYPercentage = this.avatarCropYValue;
      cropWidthPercentage = this.avatarCropWidthValue;
    }

    let cropXPixels, cropYPixels, cropSizePixels;

    if (cropXPercentage !== null && cropYPercentage !== null && cropWidthPercentage !== null) {
      cropXPixels = cropXPercentage * imgWidthPixels;
      cropYPixels = cropYPercentage * imgHeightPixels;
      cropSizePixels = cropWidthPercentage * imgWidthPixels;
    } else {
      const smallerDimPixels = Math.min(imgWidthPixels, imgHeightPixels);
      cropSizePixels = smallerDimPixels * 0.8;
      cropXPixels = (imgWidthPixels - cropSizePixels) / 2;
      cropYPixels = (imgHeightPixels - cropSizePixels) / 2;
    }

    cropSizePixels = Math.min(cropSizePixels, imgWidthPixels, imgHeightPixels);
    cropXPixels = Math.max(0, Math.min(cropXPixels, imgWidthPixels - cropSizePixels));
    cropYPixels = Math.max(0, Math.min(cropYPixels, imgHeightPixels - cropSizePixels));

    this.setCropBox(cropXPixels, cropYPixels, cropSizePixels);
  }

  setCropBox(xPixels, yPixels, sizePixels) {
    const cropBox = this.cropBoxTarget;

    const containerXPixels = xPixels + this.imageOffsetXPixels;
    const containerYPixels = yPixels + this.imageOffsetYPixels;

    cropBox.style.left = `${containerXPixels}px`;
    cropBox.style.top = `${containerYPixels}px`;
    cropBox.style.width = `${sizePixels}px`;
    cropBox.style.height = `${sizePixels}px`;

    this.currentCropXPixels = xPixels;
    this.currentCropYPixels = yPixels;
    this.currentCropSizePixels = sizePixels;

    this.updateOverlay(xPixels, yPixels, sizePixels);
  }

  updateOverlay(xPixels, yPixels, sizePixels) {
    const img = this.imageTarget;
    const imgWidthPixels = img.offsetWidth;
    const imgHeightPixels = img.offsetHeight;

    const overlay = this.overlayTarget;
    overlay.style.left = `${this.imageOffsetXPixels}px`;
    overlay.style.top = `${this.imageOffsetYPixels}px`;
    overlay.style.width = `${imgWidthPixels}px`;
    overlay.style.height = `${imgHeightPixels}px`;

    const clipPath = `polygon(
      0% 0%,
      0% 100%,
      ${xPixels}px 100%,
      ${xPixels}px ${yPixels}px,
      ${xPixels + sizePixels}px ${yPixels}px,
      ${xPixels + sizePixels}px ${yPixels + sizePixels}px,
      ${xPixels}px ${yPixels + sizePixels}px,
      ${xPixels}px 100%,
      100% 100%,
      100% 0%
    )`;
    overlay.style.clipPath = clipPath;
  }

  startDrag(event) {
    if (event.target.classList.contains("crop-handle")) {
      return;
    }

    event.preventDefault();
    this.isDragging = true;
    this.startXPixels = event.clientX;
    this.startYPixels = event.clientY;

    this.startCropXPixels = this.currentCropXPixels;
    this.startCropYPixels = this.currentCropYPixels;
    this.startCropSizePixels = this.currentCropSizePixels;
  }

  startResize(event) {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.resizeDirection = event.currentTarget.dataset.direction;
    this.startXPixels = event.clientX;
    this.startYPixels = event.clientY;

    this.startCropXPixels = this.currentCropXPixels;
    this.startCropYPixels = this.currentCropYPixels;
    this.startCropSizePixels = this.currentCropSizePixels;
  }

  handleMouseMove(event) {
    if (!this.isDragging && !this.isResizing) return;

    const img = this.imageTarget;
    const imgWidthPixels = img.offsetWidth;
    const imgHeightPixels = img.offsetHeight;

    const deltaXPixels = event.clientX - this.startXPixels;
    const deltaYPixels = event.clientY - this.startYPixels;

    if (this.isDragging) {
      let newXPixels = this.startCropXPixels + deltaXPixels;
      let newYPixels = this.startCropYPixels + deltaYPixels;
      const sizePixels = this.startCropSizePixels;

      newXPixels = Math.max(0, Math.min(newXPixels, imgWidthPixels - sizePixels));
      newYPixels = Math.max(0, Math.min(newYPixels, imgHeightPixels - sizePixels));

      this.setCropBox(newXPixels, newYPixels, sizePixels);
    } else if (this.isResizing) {
      this.handleResize(deltaXPixels, deltaYPixels, imgWidthPixels, imgHeightPixels);
    }
  }

  handleResize(deltaXPixels, deltaYPixels, imgWidthPixels, imgHeightPixels) {
    const dir = this.resizeDirection;
    let newXPixels = this.startCropXPixels;
    let newYPixels = this.startCropYPixels;
    let newSizePixels = this.startCropSizePixels;

    switch (dir) {
      case "se":
        newSizePixels = this.startCropSizePixels + Math.max(deltaXPixels, deltaYPixels);
        break;
      case "sw":
        newSizePixels = this.startCropSizePixels + Math.max(-deltaXPixels, deltaYPixels);
        newXPixels = this.startCropXPixels - (newSizePixels - this.startCropSizePixels);
        break;
      case "ne":
        newSizePixels = this.startCropSizePixels + Math.max(deltaXPixels, -deltaYPixels);
        newYPixels = this.startCropYPixels - (newSizePixels - this.startCropSizePixels);
        break;
      case "nw":
        newSizePixels = this.startCropSizePixels + Math.max(-deltaXPixels, -deltaYPixels);
        newXPixels = this.startCropXPixels - (newSizePixels - this.startCropSizePixels);
        newYPixels = this.startCropYPixels - (newSizePixels - this.startCropSizePixels);
        break;
    }

    if (newSizePixels < this.minCropSizePixels) {
      newSizePixels = this.minCropSizePixels;

      if (dir === "nw" || dir === "sw") {
        newXPixels = this.startCropXPixels + this.startCropSizePixels - this.minCropSizePixels;
      }
      if (dir === "nw" || dir === "ne") {
        newYPixels = this.startCropYPixels + this.startCropSizePixels - this.minCropSizePixels;
      }
    }

    if (newXPixels < 0) {
      newSizePixels = newSizePixels + newXPixels;
      newXPixels = 0;
    }
    if (newYPixels < 0) {
      newSizePixels = newSizePixels + newYPixels;
      newYPixels = 0;
    }
    if (newXPixels + newSizePixels > imgWidthPixels) {
      newSizePixels = imgWidthPixels - newXPixels;
    }
    if (newYPixels + newSizePixels > imgHeightPixels) {
      newSizePixels = imgHeightPixels - newYPixels;
    }

    newSizePixels = Math.min(
      newSizePixels,
      imgWidthPixels - newXPixels,
      imgHeightPixels - newYPixels,
    );
    newSizePixels = Math.max(newSizePixels, this.minCropSizePixels);

    this.setCropBox(newXPixels, newYPixels, newSizePixels);
  }

  handleMouseUp() {
    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = null;
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      this.cancelCrop();
    }
  }

  addGlobalListeners() {
    document.addEventListener("mousemove", this.boundMouseMove);
    document.addEventListener("mouseup", this.boundMouseUp);
    document.addEventListener("keydown", this.boundKeyDown);
  }

  removeGlobalListeners() {
    document.removeEventListener("mousemove", this.boundMouseMove);
    document.removeEventListener("mouseup", this.boundMouseUp);
    document.removeEventListener("keydown", this.boundKeyDown);
  }

  saveCrop() {
    this.saveCurrentCropToState();

    if (this.hasProfileXInputTarget) {
      this.profileXInputTarget.value = this.profileCropXValue?.toFixed(4) || "";
    }
    if (this.hasProfileYInputTarget) {
      this.profileYInputTarget.value = this.profileCropYValue?.toFixed(4) || "";
    }
    if (this.hasProfileWidthInputTarget) {
      this.profileWidthInputTarget.value = this.profileCropWidthValue?.toFixed(4) || "";
    }

    if (this.hasAvatarXInputTarget) {
      this.avatarXInputTarget.value = this.avatarCropXValue?.toFixed(4) || "";
    }
    if (this.hasAvatarYInputTarget) {
      this.avatarYInputTarget.value = this.avatarCropYValue?.toFixed(4) || "";
    }
    if (this.hasAvatarWidthInputTarget) {
      this.avatarWidthInputTarget.value = this.avatarCropWidthValue?.toFixed(4) || "";
    }

    this.updatePreviewRings();

    this.closeModal();
  }

  cancelCrop() {
    this.closeModal();
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.style.display = "none";
      document.body.style.overflow = "";
      this.removeGlobalListeners();
    }
  }

  updatePreviewRings() {
    if (!this.hasPreviewImageTarget) return;

    const img = this.previewImageTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    if (
      this.profileCropXValue !== null &&
      this.profileCropYValue !== null &&
      this.profileCropWidthValue !== null
    ) {
      if (!this.hasProfilePreviewRingTarget) {
        this.createProfilePreviewRing();
      }
      const ring = this.profilePreviewRingTarget;
      const x = this.profileCropXValue * 100;
      const y = this.profileCropYValue * 100;
      const width = this.profileCropWidthValue * 100;
      const height = this.profileCropWidthValue * aspectRatio * 100;
      ring.style.left = `${x}%`;
      ring.style.top = `${y}%`;
      ring.style.width = `${width}%`;
      ring.style.height = `${height}%`;
      ring.style.display = "block";
    }

    if (
      this.avatarCropXValue !== null &&
      this.avatarCropYValue !== null &&
      this.avatarCropWidthValue !== null
    ) {
      if (!this.hasAvatarPreviewRingTarget) {
        this.createAvatarPreviewRing();
      }
      const ring = this.avatarPreviewRingTarget;
      const x = this.avatarCropXValue * 100;
      const y = this.avatarCropYValue * 100;
      const width = this.avatarCropWidthValue * 100;
      const height = this.avatarCropWidthValue * aspectRatio * 100;
      ring.style.left = `${x}%`;
      ring.style.top = `${y}%`;
      ring.style.width = `${width}%`;
      ring.style.height = `${height}%`;
      ring.style.display = "block";
    }
  }

  createProfilePreviewRing() {
    const container = this.previewImageTarget.parentElement;
    const ring = document.createElement("div");
    ring.className = "crop-field-ring crop-field-ring-profile";
    ring.dataset.imageCropTarget = "profilePreviewRing";
    container.appendChild(ring);
  }

  createAvatarPreviewRing() {
    const container = this.previewImageTarget.parentElement;
    const ring = document.createElement("div");
    ring.className = "crop-field-ring crop-field-ring-avatar";
    ring.dataset.imageCropTarget = "avatarPreviewRing";
    container.appendChild(ring);
  }
}
